import React, { useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import AppLayout from '../../SideBar';
import strings from '../../../constant/strings';
import { LoadingContainer, NoDetailsContainer } from '../../../assets/styles/CommonStyles';
import { buildBuyerIdentity, formatMoney, isShopConfigured } from './utils';
import {
  addToCart, buildClient, buyNow, cartOptions, loadBuyButtonScript, makeBuyerIdentityApplier,
} from './buyButton';
import {
  BodyText, ComparePrice, DetailGrid, Description, GalleryMain, GalleryThumb, GalleryThumbs,
  HeaderRow, MutedText, OutlineButton, PageContainer, PriceText, PrimaryButton, QtyControl,
  QtyValue, SectionCard, SectionTitle, VariantChips,
} from './styles';

/**
 * The product page: gallery on the left, everything the patient needs to decide
 * on the right, in the order a shopper expects - name, price, what it does,
 * then the buttons.
 *
 * The page is ours, but the cart behind the buttons is still Shopify's: both
 * actions add to the same cart the floating widget shows, and Buy now hands
 * over to Shopify's hosted checkout.
 */
const ShopProductDetailContent = () => {
  const history = useHistory();
  const { handle } = useParams();
  const uiRef = useRef(null);
  const identifiedCarts = useRef(new Set());
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [status, setStatus] = useState(isShopConfigured() ? 'loading' : 'unavailable');
  const { userData } = useSelector((state) => ({ userData: state.auth.userData }));
  const buyerIdentity = buildBuyerIdentity(userData);
  const buyerKey = JSON.stringify(buyerIdentity);

  useEffect(() => {
    if (!isShopConfigured()) return undefined;
    let cancelled = false;
    const applyBuyerIdentity = makeBuyerIdentityApplier(buyerIdentity, identifiedCarts.current);

    loadBuyButtonScript()
      .then(() => {
        if (cancelled) return null;
        const client = buildClient();
        return client.product.fetchByHandle(handle).then((found) => {
          if (cancelled) return;
          if (!found) {
            setStatus('missing');
            return;
          }
          setProduct(found);
          setVariant((found.variants || [])[0] || null);
          setStatus('ready');
          // The cart widget carries the cart between pages and is what the
          // buttons below add to.
          const ui = window.ShopifyBuy.UI.init(client);
          uiRef.current = ui;
          ui.createComponent('cart', { options: cartOptions(applyBuyerIdentity) });
        });
      })
      .catch(() => { if (!cancelled) setStatus('error'); });

    return () => {
      cancelled = true;
      if (uiRef.current && typeof uiRef.current.destroyComponent === 'function') {
        ['cart', 'toggle'].forEach((name) => {
          try { uiRef.current.destroyComponent(name); } catch (e) { /* embed already gone */ }
        });
      }
      uiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerKey, handle]);

  const images = (product && product.images) || [];
  const price = variant && variant.price;
  const compareAt = variant && variant.compareAtPrice;
  const hasDiscount = compareAt && Number(compareAt.amount) > Number(price.amount);
  const percentOff = hasDiscount
    ? Math.round(((Number(compareAt.amount) - Number(price.amount)) / Number(compareAt.amount)) * 100)
    : 0;
  const soldOut = Boolean(variant && !variant.available);

  const run = (action, label) => {
    setNotice('');
    setBusy(label);
    Promise.resolve(action())
      .catch(() => setNotice(strings.cartActionFailed))
      .finally(() => setBusy(''));
  };

  return (
    <PageContainer>
      <HeaderRow>
        <OutlineButton onClick={() => history.push('/patient/shop')}>{strings.backToShop}</OutlineButton>
        <OutlineButton onClick={() => history.push('/patient/orders')}>{strings.myOrders}</OutlineButton>
      </HeaderRow>

      {status === 'loading' ? <LoadingContainer>{strings.loading}</LoadingContainer> : null}
      {status === 'missing' ? (
        <NoDetailsContainer><MutedText>{strings.productNotFound}</MutedText></NoDetailsContainer>
      ) : null}
      {status === 'error' || status === 'unavailable' ? (
        <NoDetailsContainer><MutedText>{strings.shopUnavailable}</MutedText></NoDetailsContainer>
      ) : null}

      {status === 'ready' && product ? (
        <SectionCard elevation={0} style={{ marginTop: 16 }}>
          <DetailGrid container spacing={4}>

            {/* Gallery */}
            <Grid item xs={12} md={5}>
              <GalleryMain>
                {images[imageIndex] && images[imageIndex].src
                  ? <img src={images[imageIndex].src} alt={product.title} />
                  : <ShoppingBagOutlinedIcon sx={{ color: '#cfcfd7', fontSize: 64 }} />}
              </GalleryMain>
              {images.length > 1 ? (
                <GalleryThumbs>
                  {images.map((image, index) => (
                    <GalleryThumb
                      key={image.id || image.src}
                      data-active={index === imageIndex ? 'true' : 'false'}
                      onClick={() => setImageIndex(index)}
                    >
                      <img src={image.src} alt={`${product.title} ${index + 1}`} />
                    </GalleryThumb>
                  ))}
                </GalleryThumbs>
              ) : null}
            </Grid>

            {/* Everything needed to decide */}
            <Grid item xs={12} md={7}>
              <SectionTitle style={{ fontSize: 20, marginBottom: 8 }}>{product.title}</SectionTitle>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <PriceText style={{ fontSize: 24 }}>
                  {formatMoney(price && price.amount, price && price.currencyCode)}
                </PriceText>
                {hasDiscount ? (
                  <>
                    <ComparePrice style={{ fontSize: 15, marginLeft: 0 }}>
                      {formatMoney(compareAt.amount, compareAt.currencyCode)}
                    </ComparePrice>
                    <BodyText style={{ color: '#2AAB6C', fontWeight: 600 }}>
                      {percentOff}% {strings.off}
                    </BodyText>
                  </>
                ) : null}
              </div>
              <MutedText style={{ marginTop: 4 }}>{strings.inclusiveOfTaxes}</MutedText>

              {/* Variants, when the product has more than one */}
              {(product.variants || []).length > 1 ? (
                <VariantChips>
                  {product.variants.map((option) => (
                    <OutlineButton
                      key={option.id}
                      size="small"
                      onClick={() => setVariant(option)}
                      style={option.id === (variant && variant.id)
                        ? { borderColor: '#1AA19C', color: '#1AA19C' } : undefined}
                    >
                      {option.title}
                    </OutlineButton>
                  ))}
                </VariantChips>
              ) : null}

              <MutedText style={{ marginTop: 12 }}>
                {soldOut ? strings.outOfStock : strings.deliveryEstimateNote}
              </MutedText>

              {/* Quantity, then the two actions a shopper expects */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '20px 0 8px', flexWrap: 'wrap' }}>
                <QtyControl>
                  <OutlineButton
                    size="small"
                    style={{ border: 0, minWidth: 36 }}
                    onClick={() => setQuantity((n) => Math.max(1, n - 1))}
                    disabled={quantity <= 1}
                  >
                    <RemoveIcon fontSize="small" />
                  </OutlineButton>
                  <QtyValue>{quantity}</QtyValue>
                  <OutlineButton
                    size="small"
                    style={{ border: 0, minWidth: 36 }}
                    onClick={() => setQuantity((n) => n + 1)}
                  >
                    <AddIcon fontSize="small" />
                  </OutlineButton>
                </QtyControl>
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <OutlineButton
                  disabled={soldOut || Boolean(busy)}
                  onClick={() => run(() => addToCart(uiRef.current, variant, quantity), 'cart')}
                >
                  {busy === 'cart' ? strings.loading : strings.addToCart}
                </OutlineButton>
                <PrimaryButton
                  disabled={soldOut || Boolean(busy)}
                  onClick={() => run(() => buyNow(uiRef.current, variant, quantity), 'buy')}
                >
                  {busy === 'buy' ? strings.redirectingToCheckout : strings.buyNow}
                </PrimaryButton>
              </div>

              {notice ? <MutedText style={{ marginTop: 12 }}>{notice}</MutedText> : null}
            </Grid>
          </DetailGrid>

          {/* The long description, below the fold as on any product page */}
          {product.descriptionHtml || product.description ? (
            <div style={{ marginTop: 32 }}>
              <SectionTitle>{strings.productDetails}</SectionTitle>
              {product.descriptionHtml
                ? <Description dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
                : <Description>{product.description}</Description>}
            </div>
          ) : null}
        </SectionCard>
      ) : null}
    </PageContainer>
  );
};

const ShopProductDetail = () => (
  <Grid container spacing={0}>
    <AppLayout>
      <ShopProductDetailContent />
    </AppLayout>
  </Grid>
);

export default ShopProductDetail;
